<template>
  <div class="horizontal-toolbar__navigation">
    <v-link
      v-for="link in tabbarLinks"
      :key="link.path"
      :class="['horizontal-toolbar__item', { '--active': isActive(link) }]"
      :to="link.path"
      :page-id="link.page_id"
      :data-mvt="linkDataSelector(link)"
      @click.native="() => handleClick(link)"
      data-role="tabbar-item"
      :data-page-id="link.page_id"
      :data-page-path="link.path"
    >
      <span class="horizontal-toolbar__text">
        {{ link.i18n_keys ? $translate(link, "title") : link.text }}
      </span>
      <unread-marker-dot v-if="isMessageTabbar(link)" />
    </v-link>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex"
import { MVT_SELECTORS } from "@/lib/globals"
import { MVT_EVENTS } from "@/sdk/event-manager"
import { standardizeUrl } from "@/lib/url-helpers"

const MESSAGES_PATH = "/me/messages"

export default {
  name: "HorizontalToolbarNavigation",
  components: {
    UnreadMarkerDot: () => import("@/components/message-thread/UnreadMarkerDot")
  },

  data() {
    return {
      MVT_SELECTORS
    }
  },

  computed: {
    ...mapGetters("tabbar", ["tabbarLinks"]),
    ...mapGetters("auth", ["isAuthenticated"])
  },

  methods: {
    ...mapActions("localisation", ["fetchTranslations"]),

    isActive(link) {
      return link.path === "/"
        ? this.$route.path === "/"
        : this.$route.path.startsWith(link.path)
    },

    isMessageTabbar(link) {
      return link.path === MESSAGES_PATH
    },

    handleClick(link) {
      window.$mvt.trackEvent(MVT_EVENTS.NAVIGATION_ITEM_CLICK, {
        data: {
          text: link.text,
          href: standardizeUrl(link.path)
        }
      })
    },

    linkDataSelector(link) {
      return link.path === "/me/my-content"
        ? MVT_SELECTORS.TABBAR_YOU
        : link.path === "/"
        ? MVT_SELECTORS.TABBAR_HOME
        : null
    }
  },

  watch: {
    isAuthenticated() {
      if (this.isAuthenticated) {
        this.tabbarLinks.forEach(link => {
          if (link.page_id) {
            this.fetchTranslations({ path: link.path })
          }
        })
      }
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";
@import "@/assets/styles/mixins";

@mixin navigation-desktop {
  .horizontal-toolbar__navigation {
    gap: var(--mvt-link-spacing--desktop, var(--mvt-link-spacing, $size--1));

    .horizontal-toolbar__item {
      padding: var(
        --mvt-link-padding--desktop,
        var(--mvt-link-padding, $size--3)
      );
    }
  }
}

.horizontal-toolbar__navigation {
  display: flex;
  align-items: center;
  gap: var(--mvt-link-spacing, $size--1);
  overflow: hidden;
}

@media screen and (min-width: $breakpoint--xl) {
  @include navigation-desktop;
}

*.--desktop-viewport {
  @include navigation-desktop;
}

.horizontal-toolbar__item {
  @include overflow-ellipsis;

  display: flex;
  align-items: center;
  position: relative;
  padding: var(--mvt-link-padding, $size--3);
  border-radius: var(--border-radius);
  text-decoration: none;
  color: $color-fixed--grey-90;
  font-size: $font-size--small;
  font-weight: 500;

  transition: background-color 0.2s ease;

  &:hover {
    cursor: pointer;
    background-color: rgba(0, 0, 0, 0.05);
  }

  &.--active {
    color: var(--primary-brand-color-hex);
    background-color: rgba(var(--primary-brand-color-rgb-vals), 0.05);
  }

  .marker-dot {
    background: $color-ui--green-dot;
    position: absolute;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    top: 2px;
    right: 8px;
    z-index: 100;
  }
}

.horizontal-toolbar__text {
  @include overflow-ellipsis;
}
</style>
